import React, { useState, useMemo } from 'react';
import { useQuery } from '@tanstack/react-query';
import { useAnalysisContext } from '../../context/AnalysisContext';
import { api } from '../../../../services/api';
import { DISEASE_CATEGORIES, DiseaseCategory } from '../../data/diseaseCategories';
import { Search, CheckCircle2, ArrowLeft, AlertCircle } from 'lucide-react';

/**
 * Step 1: Disease Selection with Categories
 *
 * Features:
 * - Two-stage selection: Category → Specific Disease
 * - Large category buttons with icons
 * - Search functionality that works across all diseases
 * - White/light theme with Silicon Valley aesthetic
 */

type SelectionStage = 'category' | 'disease';

export const Step1DiseaseSelector: React.FC = () => {
  const { selectedDisease, setSelectedDisease } = useAnalysisContext();
  const [selectionStage, setSelectionStage] = useState<SelectionStage>('category');
  const [selectedCategory, setSelectedCategory] = useState<DiseaseCategory | null>(null);
  const [searchQuery, setSearchQuery] = useState('');

  // ========================================
  // FETCH DISEASES FROM API
  // ========================================

  const { data: apiDiseases, isLoading, error } = useQuery({
    queryKey: ['diseases'],
    queryFn: api.getAvailableDiseases,
    staleTime: 10 * 60 * 1000, // 10 minutes
  });

  // ========================================
  // DYNAMIC CATEGORIES FROM API
  // ========================================

  const dynamicCategories = useMemo(() => {
    if (!apiDiseases) return DISEASE_CATEGORIES;

    // Ensure apiDiseases is an array
    const diseasesArray = Array.isArray(apiDiseases) ? apiDiseases : [];
    if (diseasesArray.length === 0) return DISEASE_CATEGORIES;

    // Build categories dynamically from API diseases
    // Only show categories that have at least one matching disease from API
    return DISEASE_CATEGORIES.map(category => ({
      ...category,
      diseases: category.diseases.filter(disease =>
        diseasesArray.some(d => d.toLowerCase() === disease.toLowerCase())
      )
    })).filter(category => category.diseases.length > 0);
  }, [apiDiseases]);

  // ========================================
  // FILTERED DISEASES
  // ========================================

  const filteredDiseases = useMemo(() => {
    if (!apiDiseases) return [];

    // Ensure apiDiseases is an array
    const diseasesArray = Array.isArray(apiDiseases) ? apiDiseases : [];

    if (selectionStage === 'category') {
      // Show all categories when searching from category view
      if (!searchQuery.trim()) {
        return [];
      }
      // If searching, jump directly to diseases that match
      const query = searchQuery.toLowerCase();
      return diseasesArray.filter(disease =>
        disease.toLowerCase().includes(query)
      );
    } else {
      // Show diseases in selected category
      if (!selectedCategory) return [];

      const categoryDiseases = selectedCategory.diseases;

      // Filter by search query if present
      if (searchQuery.trim()) {
        const query = searchQuery.toLowerCase();
        return categoryDiseases.filter(disease =>
          disease.toLowerCase().includes(query)
        );
      }

      return categoryDiseases;
    }
  }, [selectionStage, selectedCategory, searchQuery, apiDiseases]);

  // ========================================
  // HANDLERS
  // ========================================

  const handleSelectCategory = (category: DiseaseCategory) => {
    setSelectedCategory(category);
    setSelectionStage('disease');
    setSearchQuery('');
  };

  const handleSelectDisease = (disease: string) => {
    console.log('[Step1] Disease selected:', disease);
    setSelectedDisease(disease);
    console.log('[Step1] After setSelectedDisease called');
  };

  const handleBackToCategories = () => {
    setSelectionStage('category');
    setSelectedCategory(null);
    setSearchQuery('');
  };

  // ========================================
  // RENDER STATES
  // ========================================

  if (isLoading) {
    return (
      <div className="flex-1 flex items-center justify-center">
        <div className="text-center">
          <div className="inline-block animate-spin rounded-full h-12 w-12 border-b-2 border-[var(--origin-blue-500)] mb-4"></div>
          <p className="text-[var(--text-secondary)]">Loading diseases...</p>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="flex-1 flex items-center justify-center">
        <div className="origin-card-error max-w-md">
          <h3 className="text-lg font-semibold text-[var(--text-primary)] mb-2">
            Failed to Load Diseases
          </h3>
          <p className="text-[var(--text-secondary)] mb-4">
            {error instanceof Error ? error.message : 'An unexpected error occurred'}
          </p>
          <button
            onClick={() => window.location.reload()}
            className="origin-btn origin-btn-secondary"
          >
            Retry
          </button>
        </div>
      </div>
    );
  }

  // ========================================
  // MAIN RENDER
  // ========================================

  return (
    <div className="flex-1 flex flex-col max-w-7xl mx-auto w-full px-6 py-8">
      {/* Header with Back Button */}
      <div className="mb-8">
        {selectionStage === 'disease' && selectedCategory && (
          <button
            onClick={handleBackToCategories}
            className="origin-btn origin-btn-ghost flex items-center gap-2 mb-4"
          >
            <ArrowLeft className="w-4 h-4" />
            Back to Categories
          </button>
        )}

        <h2 className="text-3xl font-bold text-[var(--text-primary)] mb-2">
          {selectionStage === 'category'
            ? 'Select Disease Category'
            : `Select ${selectedCategory?.name} Disease`}
        </h2>
        <p className="text-[var(--text-secondary)] text-lg">
          {selectionStage === 'category'
            ? 'Choose a broad disease category to explore specific conditions'
            : `Choose the specific disease for your ${selectedCategory?.name.toLowerCase()} analysis`}
        </p>
      </div>

      {/* Search Bar */}
      <div className="mb-6">
        <div className="relative max-w-2xl">
          <Search className="absolute left-4 top-1/2 transform -translate-y-1/2 w-5 h-5 text-[var(--text-tertiary)]" />
          <input
            type="text"
            placeholder={
              selectionStage === 'category'
                ? "Search diseases across all categories..."
                : `Search ${selectedCategory?.name.toLowerCase()} diseases...`
            }
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            className="origin-input pl-12 w-full text-base"
          />
          {searchQuery && (
            <button
              onClick={() => setSearchQuery('')}
              className="absolute right-4 top-1/2 transform -translate-y-1/2 text-[var(--text-tertiary)] hover:text-[var(--text-primary)] transition-colors"
            >
              ✕
            </button>
          )}
        </div>
      </div>

      {/* Category Selection View */}
      {selectionStage === 'category' && !searchQuery && (
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4 pb-8">
          {dynamicCategories.map((category) => (
            <button
              key={category.id}
              onClick={() => handleSelectCategory(category)}
              className="origin-category-btn hover:transform hover:-translate-y-1 active:translate-y-0"
            >
              <h3 className="text-lg font-semibold text-[var(--text-primary)] mb-2">
                {category.name}
              </h3>
              <p className="text-sm text-[var(--text-secondary)] leading-snug">
                {category.description}
              </p>
            </button>
          ))}
        </div>
      )}

      {/* Disease Selection View (when category selected OR searching from categories) */}
      {(selectionStage === 'disease' || (selectionStage === 'category' && searchQuery)) && (
        <>
          {/* Results Count */}
          <div className="mb-4 text-sm text-[var(--text-secondary)]">
            {filteredDiseases.length > 0 ? (
              <span>Found {filteredDiseases.length} disease{filteredDiseases.length !== 1 ? 's' : ''}</span>
            ) : searchQuery ? (
              <span>No diseases found matching "{searchQuery}"</span>
            ) : (
              <span>No diseases available in this category</span>
            )}
          </div>

          {/* Disease Cards Grid */}
          {filteredDiseases.length > 0 ? (
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4 pb-8">
              {filteredDiseases.map((disease) => (
                <button
                  key={disease}
                  onClick={() => handleSelectDisease(disease)}
                  className={`origin-card text-left p-5 transition-all duration-200 hover:scale-[1.02] ${
                    selectedDisease === disease
                      ? 'ring-2 ring-[var(--origin-blue-500)] bg-[var(--origin-blue-500)]/5'
                      : ''
                  }`}
                >
                  <div className="flex items-start justify-between">
                    <h3 className={`font-semibold ${
                      selectedDisease === disease
                        ? 'text-[var(--origin-blue-500)]'
                        : 'text-[var(--text-primary)]'
                    }`}>
                      {disease}
                    </h3>
                    {selectedDisease === disease && (
                      <CheckCircle2 className="w-5 h-5 text-[var(--origin-blue-500)] flex-shrink-0" />
                    )}
                  </div>
                </button>
              ))}
            </div>
          ) : searchQuery ? (
            // No Search Results
            <div className="flex-1 flex items-center justify-center py-12">
              <div className="text-center max-w-md">
                <Search className="w-16 h-16 text-[var(--text-tertiary)] mx-auto mb-4" />
                <h3 className="text-lg font-semibold text-[var(--text-primary)] mb-2">
                  No diseases found
                </h3>
                <p className="text-[var(--text-secondary)] mb-4">
                  Try adjusting your search query or browse by category.
                </p>
                <button
                  onClick={() => setSearchQuery('')}
                  className="origin-btn origin-btn-secondary"
                >
                  Clear Search
                </button>
              </div>
            </div>
          ) : (
            // Empty Category
            <div className="flex-1 flex items-center justify-center py-12">
              <div className="text-center max-w-md">
                <AlertCircle className="w-16 h-16 text-[var(--text-tertiary)] mx-auto mb-4" />
                <h3 className="text-lg font-semibold text-[var(--text-primary)] mb-2">
                  No diseases available
                </h3>
                <p className="text-[var(--text-secondary)] mb-4">
                  This category doesn't have any diseases available in the current dataset.
                </p>
                <button
                  onClick={handleBackToCategories}
                  className="origin-btn origin-btn-secondary"
                >
                  Back to Categories
                </button>
              </div>
            </div>
          )}
        </>
      )}

      {/* Selected Disease Summary */}
      {selectedDisease && (
        <div className="fixed bottom-6 right-6 origin-card p-4 shadow-lg border-[var(--origin-blue-500)] border-2 max-w-sm">
          <div className="flex items-center gap-3">
            <CheckCircle2 className="w-5 h-5 text-[var(--origin-blue-500)] flex-shrink-0" />
            <div className="flex-1 min-w-0">
              <p className="text-xs text-[var(--text-secondary)] mb-1">Selected Disease</p>
              <p className="font-semibold text-[var(--text-primary)] truncate">
                {selectedDisease}
              </p>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default Step1DiseaseSelector;
